import { useState, useRef, useEffect } from "react";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";

const featured = [
{
  tag: "GUIDE · 5 MIN READ",
  title: "How much can you earn from Airbnb in Manchester?",
  desc: "A detailed income breakdown for Manchester landlords considering the switch to short-let.",
  author: { name: "Truestays Team", role: "Property Management" },
  img: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=900&q=80",
  href: "/resources/airbnb-earnings-manchester"
},
{
  tag: "GUIDE · 4 MIN READ",
  title: "Long-let vs short-let: which earns more in 2025?",
  desc: "We compare rental income, void periods, costs and risk so you can make an informed decision.",
  author: { name: "Truestays Team", role: "Property Management" },
  img: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=900&q=80",
  href: "/resources/long-let-vs-short-let"
},
{
  tag: "GUIDE · 6 MIN READ",
  title: "What does a short-let management company actually do?",
  desc: "Everything that happens behind the scenes, from photography to payouts, explained in plain English.",
  author: { name: "Truestays Team", role: "Property Management" },
  img: "https://images.unsplash.com/photo-1493809842364-78817add7ffb?w=900&q=80",
  href: "/resources/what-is-short-let-management"
},
{
  tag: "GUIDE · 3 MIN READ",
  title: "How to get your property Airbnb-ready in 7 days",
  desc: "A practical checklist covering photography, compliance, furnishings and listing setup.",
  author: { name: "Truestays Team", role: "Property Management" },
  img: "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=900&q=80",
  href: "/resources/airbnb-ready-checklist"
}];


const articles = [
{
  tag: "GUIDE",
  title: "How much can you earn from Airbnb in Manchester?",
  desc: "City-by-city breakdown of short-let income, occupancy rates and what affects your earnings.",
  img: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&q=80",
  href: "/resources/airbnb-earnings-manchester"
},
{
  tag: "GUIDE",
  title: "Long-let vs short-let: which earns more?",
  desc: "We compare income, costs and risk across both models so you can make an informed decision.",
  img: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=600&q=80",
  href: "/resources/long-let-vs-short-let"
},
{
  tag: "GUIDE",
  title: "What does a short-let management company actually do?",
  desc: "From photography to payouts, everything behind the scenes explained in plain English.",
  img: "https://images.unsplash.com/photo-1493809842364-78817add7ffb?w=600&q=80",
  href: "/resources/what-is-short-let-management"
},
{
  tag: "GUIDE",
  title: "How to get your property Airbnb-ready in 7 days",
  desc: "A practical checklist covering photography, compliance, furnishings and listing setup.",
  img: "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=600&q=80",
  href: "/resources/airbnb-ready-checklist"
},
{
  tag: "GUIDE",
  title: "UK short-let regulations: what landlords need to know",
  desc: "Planning permission, 90-day rules, council requirements and how to stay compliant.",
  img: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=600&q=80",
  href: "/resources/short-let-regulations-uk"
},
{
  tag: "GUIDE",
  title: "Dynamic pricing explained: how we maximise your income",
  desc: "How algorithmic pricing adjusts your rates daily to maximise occupancy and revenue.",
  img: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&q=80",
  href: "/resources/dynamic-pricing-guide"
}];


export default function ResourcesSection() {
  const [active, setActive] = useState(0);
  const trackRef = useRef(null);

  const prev = () => setActive((a) => Math.max(0, a - 1));
  const next = () => setActive((a) => Math.min(featured.length - 1, a + 1));

  useEffect(() => {
    if (trackRef.current) {
      trackRef.current.scrollTo({ left: active * trackRef.current.offsetWidth, behavior: "smooth" });
    }
  }, [active]);

  return (
    <section style={{
      backgroundColor: "#F4F1E7",
      paddingTop: "clamp(60px, 7vw, 96px)",
      paddingBottom: "clamp(60px, 7vw, 96px)"
    }}>
      {/* Header */}
      <div style={{ maxWidth: 1200, margin: "0 auto", paddingLeft: "clamp(20px, 5vw, 80px)", paddingRight: "clamp(20px, 5vw, 80px)", marginBottom: 32 }}>
        <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: "#5C7268", marginBottom: 10, marginTop: 0 }}>
          Resources
        </p>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h2 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: "clamp(22px, 3vw, 36px)", letterSpacing: "-0.02em", color: "#112215", margin: 0 }}>
            Popular Resources
          </h2>
          <Link to="/resources" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 500, color: "#5C7268", textDecoration: "none", display: "flex", alignItems: "center", gap: 6, transition: "color 0.2s" }}
          onMouseEnter={(e) => e.currentTarget.style.color = "#112215"}
          onMouseLeave={(e) => e.currentTarget.style.color = "#5C7268"}>
            
            View all <ArrowRight size={14} />
          </Link>
        </div>
      </div>

      {/* Featured slider — full width with side padding */}
      <div style={{ paddingLeft: "clamp(20px, 5vw, 80px)", paddingRight: "clamp(20px, 5vw, 80px)", marginBottom: 16 }} className="hidden">
        <div style={{ maxWidth: 1200, margin: "0 auto", position: "relative" }}>
          {/* Overflow hidden viewport */}
          <div ref={trackRef} style={{ overflow: "hidden", borderRadius: 16 }}>
            <div style={{ display: "flex", width: `${featured.length * 100}%`, transition: "none" }}>
              {featured.map((item, i) =>
              <div
                key={i}
                style={{
                  width: `${100 / featured.length}%`,
                  flexShrink: 0,
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  background: "#fff",
                  borderRadius: 16,
                  overflow: "hidden",
                  minHeight: 320
                }}
                className="featured-card-grid">
                
                  {/* Text side */}
                  <div style={{ padding: "48px 40px", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                    <div>
                      <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", color: "#888", textTransform: "uppercase", marginBottom: 16, marginTop: 0 }}>{item.tag}</p>
                      <h3 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: "clamp(20px, 2.5vw, 28px)", lineHeight: 1.25, color: "#1A1A1A", marginTop: 0, marginBottom: 16, letterSpacing: "-0.02em" }}>{item.title}</h3>
                      <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 15, color: "#666", lineHeight: 1.7, margin: 0 }}>{item.desc}</p>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 32 }}>
                      <div style={{ width: 36, height: 36, borderRadius: "50%", background: "#F0F0F0", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <span style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 13, color: "#555" }}>T</span>
                      </div>
                      <div>
                        <p style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: 13, color: "#1A1A1A", margin: 0 }}>{item.author.name}</p>
                        <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "#888", margin: "2px 0 0" }}>{item.author.role}</p>
                      </div>
                    </div>
                    <Link to={item.href} style={{ display: "inline-flex", alignItems: "center", gap: 6, marginTop: 20, fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 600, color: "#4A6DC7", textDecoration: "none" }}
                  onMouseEnter={(e) => e.currentTarget.style.gap = "10px"}
                  onMouseLeave={(e) => e.currentTarget.style.gap = "6px"}>
                    
                      Read article <ArrowRight size={14} />
                    </Link>
                  </div>
                  {/* Image side */}
                  <div style={{ overflow: "hidden" }}>
                    <img src={item.img} alt={item.title} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Dots + arrows */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 16 }}>
            <div style={{ display: "flex", gap: 6 }}>
              {featured.map((_, i) =>
              <button key={i} onClick={() => setActive(i)} style={{ width: i === active ? 24 : 8, height: 8, borderRadius: 100, background: i === active ? "#1A1A1A" : "#D5D5D5", border: "none", cursor: "pointer", transition: "all 0.25s", padding: 0 }} />
              )}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={prev} disabled={active === 0} style={{ width: 36, height: 36, borderRadius: "50%", border: "1.5px solid #E5E5E5", background: "#fff", cursor: active === 0 ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", opacity: active === 0 ? 0.4 : 1, transition: "opacity 0.2s" }}>
                <ChevronLeft size={16} color="#1A1A1A" />
              </button>
              <button onClick={next} disabled={active === featured.length - 1} style={{ width: 36, height: 36, borderRadius: "50%", border: "1.5px solid #E5E5E5", background: "#fff", cursor: active === featured.length - 1 ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", opacity: active === featured.length - 1 ? 0.4 : 1, transition: "opacity 0.2s" }}>
                <ChevronRight size={16} color="#1A1A1A" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Article grid */}
      <div style={{ maxWidth: 1200, margin: "48px auto 0", paddingLeft: "clamp(20px, 5vw, 80px)", paddingRight: "clamp(20px, 5vw, 80px)" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 28 }} className="resources-grid">
          {articles.slice(0, 3).map((r, i) =>
          <Link
            key={i}
            to={r.href}
            style={{ textDecoration: "none", display: "flex", flexDirection: "column" }}>
            
              <div style={{ height: 190, borderRadius: 12, overflow: "hidden", marginBottom: 16 }}>
                <img
                src={r.img}
                alt={r.title}
                style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", transition: "transform 0.4s ease" }}
                onMouseEnter={(e) => e.currentTarget.style.transform = "scale(1.04)"}
                onMouseLeave={(e) => e.currentTarget.style.transform = "scale(1)"} />
              
              </div>
              <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#888", marginBottom: 8, display: "block" }}>{r.tag}</span>
              <h3 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 600, fontSize: 15, color: "#1A1A1A", lineHeight: 1.4, marginTop: 0, marginBottom: 6 }}>{r.title}</h3>
              <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#666", lineHeight: 1.65, margin: 0 }}>{r.desc}</p>
            </Link>
          )}
        </div>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .resources-grid { grid-template-columns: 1fr !important; }
          .featured-card-grid { grid-template-columns: 1fr !important; }
        }
        @media (min-width: 769px) and (max-width: 1000px) {
          .resources-grid { grid-template-columns: repeat(2, 1fr) !important; }
        }
      `}</style>
    </section>);

}